import type { ProposalNodeSet, RfqPackage, RfqProject, SupplierShipToAddress, TimberListItem, TimberRfqDrynessSpec } from '../types';
import { formatSupplierShipToAddress } from './supplierShipTo';
import { waitForDocumentImages, waitForNextPaint } from './waitForDocumentImages';
import { speciesLabelMatchesRequested, rfqProjectTimberSpeciesDrynessEntries } from './timberRfqSuppliers';
import {
  DEFAULT_FALLBACK_GRADE,
  SPECIES_DEFAULT_GRADE,
  isDefaultSpeciesGrade,
  speciesLabelForMatching,
} from './timberMaterialParser';
import {
  convertToTimberListItems,
  groupItemsByGroup,
  isCadworkTimberCsv,
  parseProposalCSV,
} from './proposalCSVParser';
import {
  requestedEntriesForStructure,
  structureSpeciesRuleFor,
  structureSpeciesRulesFromInvite,
  structureSpeciesRulesFromPackage,
  type StructureSpeciesRule,
} from './timberRfqStructureSpecies';

export { isCadworkTimberCsv };

/** Minimal proposal shape required for supplier CSV / print (timber lists only). */
export interface SupplierTimberExportState {
  nodeSets: ProposalNodeSet[];
}

export type TimberSupplierExportOptions = {
  /** RFQ invite species/dryness for this supplier — drives PDF/CSV headers and line labels. */
  requestedSpeciesEntries?: TimberSpeciesDrynessEntry[];
  /** Species narrowed per structure (invite `structureSpecies`); a structure without a rule gets every requested species. */
  structureSpecies?: StructureSpeciesRule[];
  /** Delivery address shown on the timber RFQ PDF. */
  shipToAddress?: SupplierShipToAddress;
};

/**
 * Requested entries for one structure, or null when a per-structure rule leaves
 * nothing to price there (the structure is then skipped, not exported unfiltered).
 */
function requestedEntriesForNodeSet(
  ns: Pick<ProposalNodeSet, 'id' | 'name'>,
  options: TimberSupplierExportOptions | undefined,
): TimberSpeciesDrynessEntry[] | null {
  const all = options?.requestedSpeciesEntries ?? [];
  if (!all.length || !options?.structureSpecies?.length) return all;
  const mine = requestedEntriesForStructure(ns, all, options.structureSpecies);
  return mine.length ? mine : null;
}

export type TimberExportSpeciesGroup = {
  displaySpecies: string;
  drynessSpec: SelectableTimberRfqDrynessSpec;
  items: TimberListItem[];
};

/** Alternate species quote (structure's "Quote Additional Species" tabs). */
export type TimberExportAlternateSpecies = {
  speciesName?: string;
  timberList?: TimberListItem[];
};

/** Alternate species lists to consider for export — only when multi-species quoting is on. */
export function nodeSetExportAlternateSpecies(ns: ProposalNodeSet): TimberExportAlternateSpecies[] {
  if (!ns.materials.quoteMultipleSpecies) return [];
  return ns.materials.alternateSpecies || [];
}

export const TIMBER_SUPPLIER_POPUP_BLOCKED_MSG =
  'Your browser blocked the print window. Allow pop-ups for this site, then try Export Timber PDF again.';

/** Standard RFQ specs shown in timber RFQ emails and the supplier portal. */
export const DEFAULT_TIMBER_RFQ_DRYNESS_SPEC = 'kiln_dried' satisfies TimberRfqDrynessSpec;

export const SELECTABLE_TIMBER_RFQ_DRYNESS_SPECS = ['green', 'kiln_dried'] as const;
export type SelectableTimberRfqDrynessSpec = (typeof SELECTABLE_TIMBER_RFQ_DRYNESS_SPECS)[number];

/** @deprecated Use {@link timberRfqDrynessSpecLabel} with project dryness spec. */
export const TIMBER_SUPPLIER_DRYNESS_SPEC = 'Kiln-Dried';
export const TIMBER_SUPPLIER_DIMENSIONS_SPEC = '1/2" Under Listed Dimensions';
export const TIMBER_SUPPLIER_TRUE_DIMENSIONS_SPEC = 'True dimensions';
export const TIMBER_SUPPLIER_SURFACE_SPEC = 'S4S';

/** Dimensions line on a vendor quote. True dimensions replaces the half-inch-under spec. */
export function timberSupplierDimensionsSpecLabel(trueDimensions?: boolean): string {
  return trueDimensions ? TIMBER_SUPPLIER_TRUE_DIMENSIONS_SPEC : TIMBER_SUPPLIER_DIMENSIONS_SPEC;
}

/**
 * One dimensions line for every list on a supplier quote.
 * A mix names the true-dimension lists and keeps the half-inch spec on the rest.
 */
export function timberSupplierDimensionsSpecForLists(
  lists: Array<{ trueDimensions?: boolean; label?: string }>,
): string {
  if (!lists.length) return TIMBER_SUPPLIER_DIMENSIONS_SPEC;
  const exact = lists.filter((list) => list.trueDimensions);
  if (exact.length === 0) return TIMBER_SUPPLIER_DIMENSIONS_SPEC;
  if (exact.length === lists.length) return TIMBER_SUPPLIER_TRUE_DIMENSIONS_SPEC;
  const names = exact.map((list) => (list.label || '').trim()).filter(Boolean);
  const who = names.length ? ` on ${names.join(', ')}` : '';
  return `True dimensions${who}. Other lists are 1/2" under listed dimensions.`;
}

/** Map legacy/combined values to a single green or kiln-dried spec. */
export function normalizeTimberRfqDrynessSpec(
  spec?: TimberRfqDrynessSpec | null,
): SelectableTimberRfqDrynessSpec {
  if (spec === 'green' || spec === 'kiln_dried') return spec;
  return DEFAULT_TIMBER_RFQ_DRYNESS_SPEC;
}

export function resolveTimberRfqDrynessSpec(
  spec?: TimberRfqDrynessSpec | null,
): SelectableTimberRfqDrynessSpec {
  return normalizeTimberRfqDrynessSpec(spec);
}

export function timberRfqDrynessSpecLabel(spec?: TimberRfqDrynessSpec | null): string {
  switch (normalizeTimberRfqDrynessSpec(spec)) {
    case 'green':
      return 'Green';
    case 'kiln_dried':
      return 'Kiln-Dried';
  }
}

export type TimberSpeciesDrynessEntry = {
  species: string;
  drynessSpec: SelectableTimberRfqDrynessSpec;
};

/** Human-readable dryness for RFQ specs when each species may differ. */
export function timberSpeciesDrynessLabel(entries: TimberSpeciesDrynessEntry[]): string {
  if (!entries.length) return timberRfqDrynessSpecLabel(null);
  const normalized = entries.map(entry => ({
    species: entry.species,
    drynessSpec: normalizeTimberRfqDrynessSpec(entry.drynessSpec),
  }));
  const unique = new Set(normalized.map(entry => entry.drynessSpec));
  if (unique.size === 1) return timberRfqDrynessSpecLabel(normalized[0].drynessSpec);
  return normalized
    .map(entry => `${entry.species} — ${timberRfqDrynessSpecLabel(entry.drynessSpec)}`)
    .join('; ');
}

/**
 * Grade cell for PDF/CSV export. When the staged list is quoted as a different
 * species (Manage Suppliers relabel), map the source species' default grade to
 * the destination species default (EWP #2 → Douglas Fir #1). Explicit grades
 * like Select are kept.
 */
export function gradeCellForExportItem(
  item: Pick<TimberListItem, 'species' | 'grade'>,
  displaySpecies: string,
): string {
  const grade = item.grade?.trim() || '';
  const target = displaySpecies.trim();
  if (!target) return grade;

  const itemSpecies = item.species?.trim() || '';
  if (itemSpecies && speciesLabelMatchesRequested(itemSpecies, [target])) {
    return grade;
  }

  const sourceCanonical = itemSpecies ? speciesLabelForMatching(itemSpecies) : '';
  const targetCanonical = speciesLabelForMatching(target);
  if (!grade || (sourceCanonical && isDefaultSpeciesGrade(grade, sourceCanonical))) {
    return SPECIES_DEFAULT_GRADE[targetCanonical] ?? DEFAULT_FALLBACK_GRADE;
  }
  return grade;
}

/** Summary Grade line — per species when grades differ (mirrors dryness). */
export function timberSpeciesGradeLabel(
  groups: Array<{ displaySpecies: string; items: Array<Pick<TimberListItem, 'species' | 'grade'>> }>,
): string {
  if (!groups.length) return 'Not specified';
  const perSpecies = groups.map(group => {
    const grades = Array.from(
      new Set(
        group.items
          .map(item => gradeCellForExportItem(item, group.displaySpecies).trim())
          .filter(Boolean),
      ),
    ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    return {
      species: group.displaySpecies,
      gradePart: grades.length ? grades.join(', ') : 'Not specified',
    };
  });
  const unique = new Set(perSpecies.map(row => row.gradePart));
  if (unique.size === 1) return perSpecies[0]!.gradePart;
  return perSpecies.map(row => `${row.species} — ${row.gradePart}`).join('; ');
}

export function timberSpeciesDrynessEntriesAreMixed(entries: TimberSpeciesDrynessEntry[]): boolean {
  if (entries.length <= 1) return false;
  const normalized = entries.map(entry => normalizeTimberRfqDrynessSpec(entry.drynessSpec));
  return new Set(normalized).size > 1;
}

/** Plain-text dryness line for email detail (single label or semicolon list). */
export function formatTimberSpeciesDrynessForEmailDetail(entries: TimberSpeciesDrynessEntry[]): string {
  return timberSpeciesDrynessLabel(entries);
}

/**
 * HTML fragment for the Dryness row in RFQ publish emails.
 * Mixed species get a nested list; uniform dryness stays a single inline label.
 */
export function formatTimberSpeciesDrynessForEmailDetailHtml(entries: TimberSpeciesDrynessEntry[]): string {
  if (!entries.length) return escapeHtml(timberRfqDrynessSpecLabel(null));
  if (!timberSpeciesDrynessEntriesAreMixed(entries)) {
    return escapeHtml(timberRfqDrynessSpecLabel(entries[0].drynessSpec));
  }
  const items = entries
    .map(
      entry =>
        `<li>${escapeHtml(entry.species)} — ${escapeHtml(timberRfqDrynessSpecLabel(entry.drynessSpec))}</li>`,
    )
    .join('');
  return `<ul style="margin: 6px 0 0; padding-left: 18px; color: #4F423A; font-size: 14px; line-height: 1.55;">${items}</ul>`;
}

/** Human-readable dryness for supplier CSV/PDF line items. */
export function formatTimberMoistureLabel(moistureContent: TimberListItem['moistureContent'] | undefined): string {
  if (moistureContent === 'green') return 'Green';
  if (moistureContent === 'kiln_dried') return 'Kiln dried';
  return '';
}

export function collectStructureTimberMeta(ns: ProposalNodeSet): {
  speciesLabels: string[];
  gradeLabels: string[];
  drynessLabels: string[];
} {
  const species = new Set<string>();
  const grades = new Set<string>();
  const dryness = new Set<string>();

  const scanItem = (item: TimberListItem) => {
    if (item.species?.trim()) species.add(item.species.trim());
    if (item.grade?.trim()) grades.add(item.grade.trim());
    const dry = formatTimberMoistureLabel(item.moistureContent);
    if (dry) dryness.add(dry);
  };

  (ns.materials.timberList || []).forEach(scanItem);
  (ns.materials.alternateSpecies || []).forEach(config => {
    if (config.speciesName?.trim()) species.add(config.speciesName.trim());
    (config.timberList || []).forEach(scanItem);
  });

  const sortCi = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });
  return {
    speciesLabels: Array.from(species).sort(sortCi),
    gradeLabels: Array.from(grades).sort(sortCi),
    drynessLabels: Array.from(dryness).sort(sortCi),
  };
}

function nodeSetsIncludedInProposal(nodeSets: ProposalNodeSet[]): ProposalNodeSet[] {
  return nodeSets.filter(ns => ns.enabled !== false);
}

/**
 * Spline and wedge rows carry their own material (often oak on a pine or fir frame).
 * Quoting the frame as another species must not rewrite those lines.
 */
export function isSplineOrWedgeTimberName(name: string | undefined): boolean {
  return /(?:^|[^a-z0-9])(?:splines?|wedges?)(?:[^a-z0-9]|$)/i.test(name || '');
}

export function lineKeepsListedSpecies(
  item: { name?: string; species?: string },
  requestedSpecies: string[],
): boolean {
  const requested = requestedSpecies.map(species => species.trim()).filter(Boolean);
  if (!requested.length || !isSplineOrWedgeTimberName(item.name)) return false;
  const own = item.species?.trim() || '';
  if (!own) return false;
  return !requested.some(species => speciesLabelMatchesRequested(own, [species]));
}

/** Species cell for one exported line. Frame lines use the requested species. */
export function speciesCellForExportItem(
  item: { name?: string; species?: string },
  displaySpecies: string,
): string {
  const target = displaySpecies.trim();
  const own = item.species?.trim() || '';
  if (target && lineKeepsListedSpecies(item, [target])) return own;
  return target || own;
}

/**
 * Group timber lines for export — Manage Suppliers requested species labels the
 * full staged timber list (quote every piece as that species). Spline and wedge
 * rows keep the species on the list when it is not the requested species.
 * When a structure has true alternateSpecies lists (multi-species quoting),
 * those lists win for matching requested species.
 */
export function resolveTimberExportSpeciesGroups(
  timberList: TimberListItem[],
  requestedEntries: TimberSpeciesDrynessEntry[] = [],
  alternateSpecies: TimberExportAlternateSpecies[] = [],
): TimberExportSpeciesGroup[] {
  if (!requestedEntries.length) {
    const pool = [
      ...timberList,
      ...alternateSpecies.flatMap(alt => alt.timberList || []),
    ];
    const bySpecies: Record<string, TimberListItem[]> = {};
    pool.forEach(item => {
      const species = item.species?.trim() || 'Unspecified';
      if (!bySpecies[species]) bySpecies[species] = [];
      bySpecies[species].push(item);
    });
    return Object.entries(bySpecies).map(([displaySpecies, items]) => ({
      displaySpecies,
      drynessSpec: DEFAULT_TIMBER_RFQ_DRYNESS_SPEC,
      items,
    }));
  }

  const groups: TimberExportSpeciesGroup[] = [];
  for (const entry of requestedEntries) {
    const alt = alternateSpecies.find(a =>
      speciesLabelMatchesRequested(a.speciesName || '', [entry.species]),
    );
    // Prefer a dedicated alternate list when present; otherwise send the entire
    // staged list relabeled as the supplier's requested species.
    const items = alt?.timberList?.length ? alt.timberList : timberList;
    if (items.length) {
      groups.push({ displaySpecies: entry.species, drynessSpec: entry.drynessSpec, items });
    }
  }
  return groups;
}

/**
 * Per-line dryness cell — the proposal's own moisture content wins (structures can
 * mix KD and GRN, e.g. KD lintels on an otherwise green frame), falling back to the
 * RFQ invite's per-species dryness for lines without explicit moisture. Matches the
 * email-attachment CSV built in functions/src/rfqPublishTimberCsv.ts.
 */
function drynessCellForExportItem(
  item: TimberListItem,
  drynessSpec: SelectableTimberRfqDrynessSpec,
  keepListedSpecies = false,
): string {
  const own = formatTimberMoistureLabel(item.moistureContent);
  if (own) return own;
  // A spline or wedge quoted in its own species does not inherit the frame dryness.
  if (keepListedSpecies) return '—';
  return timberRfqDrynessSpecLabel(drynessSpec) || '—';
}

export const SUPPLIER_TIMBER_CSV_PREVIEW_COLUMNS = [
  'Structure',
  'Name',
  'Species',
  'Grade',
  'Dryness',
  'Dimensions',
  'Quantity',
  'Board Feet',
] as const;

/** Rows for supplier publish CSV preview (matches email attachment format). */
export function buildTimberSupplierCsvPreviewTable(
  state: SupplierTimberExportState,
  options?: TimberSupplierExportOptions,
  maxRows = Number.MAX_SAFE_INTEGER,
): { columns: readonly string[]; rows: string[][]; totalRows: number } {
  const rows: string[][] = [];
  let totalRows = 0;

  nodeSetsIncludedInProposal(state.nodeSets).forEach(ns => {
    const requestedEntries = requestedEntriesForNodeSet(ns, options);
    if (!requestedEntries) return;
    const speciesGroups = resolveTimberExportSpeciesGroups(
      ns.materials.timberList || [],
      requestedEntries,
      nodeSetExportAlternateSpecies(ns),
    );
    speciesGroups.forEach(({ displaySpecies, drynessSpec, items }) => {
      items.forEach(item => {
        totalRows += 1;
        if (rows.length >= maxRows) return;
        const speciesCell = speciesCellForExportItem(item, displaySpecies);
        const keepListedSpecies = !!displaySpecies.trim() && speciesCell !== displaySpecies.trim();
        rows.push([
          ns.name,
          item.name,
          speciesCell,
          gradeCellForExportItem(item, speciesCell),
          drynessCellForExportItem(item, drynessSpec, keepListedSpecies),
          item.dimensions || '',
          String(item.quantity),
          String(item.boardFeet),
        ]);
      });
    });
  });

  return { columns: SUPPLIER_TIMBER_CSV_PREVIEW_COLUMNS, rows, totalRows };
}

export function timberSupplierExportHasContent(state: SupplierTimberExportState): boolean {
  return nodeSetsIncludedInProposal(state.nodeSets).some(
    ns =>
      (ns.materials.timberList || []).length > 0
      || nodeSetExportAlternateSpecies(ns).some(alt => (alt.timberList || []).length > 0),
  );
}

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function timberSupplierLogoUrl(): string {
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  const prefix = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${base}${prefix}/assets/logo/LCTF%20Profile.png`;
}

export function buildTimberSupplierRequestHtml(
  state: SupplierTimberExportState,
  dealTitle: string | undefined,
  generatedAt: Date = new Date(),
  logoUrl?: string,
  options?: TimberSupplierExportOptions,
): string {
  const logoSrc = logoUrl ?? timberSupplierLogoUrl();
  let html = `
      <html>
      <head>
        <title>Timber List - ${escapeHtml(dealTitle || 'Proposal')}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          .header { display: flex; align-items: center; gap: 20px; margin-bottom: 20px; padding-bottom: 20px; border-bottom: 2px solid #2c3e2f; }
          .logo { width: 80px; height: 80px; object-fit: contain; }
          .company-info { flex: 1; }
          .company-name { font-size: 20px; font-weight: bold; color: #2c3e2f; margin: 0 0 4px 0; }
          .company-contact { font-size: 11px; color: #555; margin: 2px 0; }
          .company-contact a { color: #2c3e2f; text-decoration: none; }
          h1 { color: #2c3e2f; margin-bottom: 5px; font-size: 18px; }
          h2 { color: #2c3e2f; margin-top: 30px; margin-bottom: 10px; border-bottom: 2px solid #2c3e2f; padding-bottom: 5px; }
          h3 { color: #555; margin-top: 20px; margin-bottom: 8px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          th { background: #2c3e2f; color: white; padding: 8px; text-align: left; font-size: 12px; }
          td { border: 1px solid #ddd; padding: 6px 8px; font-size: 11px; }
          tr:nth-child(even) { background: #f9f9f9; }
          .totals { font-weight: bold; background: #f0f0f0; }
          .date { color: #666; font-size: 12px; }
          .project { color: #444; font-size: 14px; margin-bottom: 10px; }
          .note { background: #fff3cd; border: 1px solid #ffc107; padding: 12px 16px; border-radius: 6px; margin: 16px 0 24px 0; font-size: 12px; font-weight: 600; color: #856404; }
          .empty { color: #666; font-style: italic; margin: 24px 0; }
        </style>
      </head>
      <body>
        <div class="header">
          <img src="${escapeHtml(logoSrc)}" class="logo" alt="LCTF Logo" onerror="this.style.display='none'" />
          <div class="company-info">
            <p class="company-name">Leiper's Creek Timber Frames</p>
            <p class="company-contact"><a href="https://www.lctimberframes.com">www.lctimberframes.com</a></p>
            <p class="company-contact"><a href="mailto:info@lctimberframes.com">info@lctimberframes.com</a></p>
            <p class="company-contact">615-716-8490</p>
          </div>
        </div>
        <h1>Timber List - Request for Quote</h1>
        <p class="project"><strong>Project:</strong> ${escapeHtml(dealTitle || 'Proposal')}</p>
        ${
          options?.shipToAddress?.line1?.trim()
            ? `<p class="project"><strong>Ship to:</strong> ${escapeHtml(formatSupplierShipToAddress(options.shipToAddress))}</p>`
            : ''
        }
        <p class="date">Generated: ${generatedAt.toLocaleDateString()}</p>
        <div class="note">ALL TIMBERS TO BE QUOTED S4S AND SIZED 1/2 INCH UNDER LISTED DIMENSIONS.</div>
    `;

  let sectionCount = 0;
  nodeSetsIncludedInProposal(state.nodeSets).forEach(ns => {
    const requestedEntries = requestedEntriesForNodeSet(ns, options);
    if (!requestedEntries) return;
    const timberList = ns.materials.timberList || [];
    const speciesGroups = resolveTimberExportSpeciesGroups(
      timberList,
      requestedEntries,
      nodeSetExportAlternateSpecies(ns),
    );
    if (speciesGroups.length === 0) return;
    sectionCount += 1;
    const { drynessLabels } = collectStructureTimberMeta(ns);
    const speciesList = requestedEntries.length
      ? requestedEntries.map(entry => entry.species).join(', ')
      : collectStructureTimberMeta(ns).speciesLabels.join(', ') || 'Not specified';
    const gradeList = timberSpeciesGradeLabel(speciesGroups);
    // This structure's own moisture wins (structures can mix KD and GRN); the
    // invite's per-species dryness is the fallback when lines don't specify.
    const drynessList =
      drynessLabels.join(', ')
      || (requestedEntries.length ? timberSpeciesDrynessLabel(requestedEntries) : 'Not specified');

    html += `<h2>${escapeHtml(ns.name)}${(ns.quantity || 1) > 1 ? ` (×${ns.quantity})` : ''}</h2>`;
    html += `<p style="font-size: 12px; color: #2c3e2f; margin: 4px 0 0 0;"><strong>Species requested:</strong> ${escapeHtml(speciesList)}</p>`;
    html += `<p style="font-size: 12px; color: #2c3e2f; margin: 4px 0 0 0;"><strong>Grade:</strong> ${escapeHtml(gradeList)}</p>`;
    html += `<p style="font-size: 12px; color: #2c3e2f; margin: 4px 0 12px 0;"><strong>Dryness:</strong> ${escapeHtml(drynessList)}</p>`;

    speciesGroups.forEach(({ displaySpecies, drynessSpec, items }) => {
      html += `<h3>${escapeHtml(displaySpecies)}</h3>`;
      html += `<table>
          <thead><tr><th>Name</th><th>Grade</th><th>Dryness</th><th>Dimensions</th><th>Qty</th><th>BdFt Each</th><th>Total BdFt</th></tr></thead>
          <tbody>`;

      let speciesBdFt = 0;
      let speciesQty = 0;
      items.forEach(item => {
        speciesBdFt += item.boardFeet * item.quantity;
        speciesQty += item.quantity;
        const speciesCell = speciesCellForExportItem(item, displaySpecies);
        const keepListedSpecies = !!displaySpecies.trim() && speciesCell !== displaySpecies.trim();
        const dryCell = drynessCellForExportItem(item, drynessSpec, keepListedSpecies);
        const gradeCell = gradeCellForExportItem(item, speciesCell);
        const nameCell = keepListedSpecies ? `${item.name} (${speciesCell})` : item.name;
        html += `<tr>
            <td>${escapeHtml(nameCell)}</td>
            <td>${escapeHtml(gradeCell)}</td>
            <td>${escapeHtml(dryCell)}</td>
            <td>${escapeHtml(item.dimensions)}</td>
            <td>${item.quantity}</td>
            <td>${item.boardFeet}</td>
            <td>${(item.boardFeet * item.quantity).toFixed(0)}</td>
          </tr>`;
      });

      html += `<tr class="totals">
          <td colspan="4">Subtotal - ${escapeHtml(displaySpecies)}</td>
          <td>${speciesQty}</td>
          <td></td>
          <td>${speciesBdFt.toFixed(0)}</td>
        </tr>`;
      html += `</tbody></table>`;
    });
  });

  if (sectionCount === 0) {
    html += `<p class="empty">No timber lines on enabled structures. Add timber lists in structure details, then export again.</p>`;
  }

  html += `</body></html>`;
  return html;
}

export function downloadTimberSupplierCsv(
  state: SupplierTimberExportState,
  filenameBase: string,
  options?: TimberSupplierExportOptions,
): void {
  const csv = buildTimberSupplierCsvText(state, options);
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filenameBase.replace(/[^\w-]+/g, '_') || 'timber-list'}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Filename on the timber RFQ email attachment (`label.csv`, spaces and punctuation folded). */
export function timberRfqEmailAttachmentFilename(label: string): string {
  const base = label.replace(/\.csv$/i, '').replace(/[^\w\-]+/g, '_').replace(/^_+|_+$/g, '') || 'timber-list';
  return `${base}.csv`;
}

/** Build supplier-format CSV text from proposal node sets. */
export function buildTimberSupplierCsvText(
  state: SupplierTimberExportState,
  options?: TimberSupplierExportOptions,
): string {
  const rows: string[] = [];
  rows.push('Structure,Name,Species,Grade,Dryness,Dimensions,Quantity,Board Feet');

  nodeSetsIncludedInProposal(state.nodeSets).forEach(ns => {
    const requestedEntries = requestedEntriesForNodeSet(ns, options);
    if (!requestedEntries) return;
    const speciesGroups = resolveTimberExportSpeciesGroups(
      ns.materials.timberList || [],
      requestedEntries,
      nodeSetExportAlternateSpecies(ns),
    );
    speciesGroups.forEach(({ displaySpecies, drynessSpec, items }) => {
      items.forEach(item => {
        const speciesCell = speciesCellForExportItem(item, displaySpecies);
        const keepListedSpecies = !!displaySpecies.trim() && speciesCell !== displaySpecies.trim();
        const itemDryness = drynessCellForExportItem(item, drynessSpec, keepListedSpecies);
        const itemGrade = gradeCellForExportItem(item, speciesCell);
        rows.push(
          `"${ns.name.replace(/"/g, '""')}","${item.name.replace(/"/g, '""')}","${speciesCell.replace(/"/g, '""')}","${itemGrade.replace(/"/g, '""')}","${itemDryness.replace(/"/g, '""')}","${(item.dimensions || '').replace(/"/g, '""')}",${item.quantity},${item.boardFeet}`,
        );
      });
    });
  });

  return rows.join('\n');
}

function parseSimpleCsvRow(line: string): string[] {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i]!;
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === ',' && !inQuotes) {
      cells.push(current);
      current = '';
      continue;
    }
    current += ch;
  }
  cells.push(current);
  return cells;
}

/** Convert Cadwork timber CSV to supplier RFQ CSV with parsed Species + Grade columns. */
export function buildSupplierCsvTextFromCadworkCsv(
  csvText: string,
  requestedSpecies: string[] = [],
  options?: { trueDimensions?: boolean },
): string {
  const parsed = parseProposalCSV(csvText, options);
  if (!parsed.items.length) return csvText;

  const grouped = groupItemsByGroup(parsed.items);
  const speciesLabels = requestedSpecies.map(s => s.trim()).filter(Boolean);
  const labels = speciesLabels.length ? speciesLabels : [''];
  const rows: string[] = ['Structure,Name,Species,Grade,Dryness,Dimensions,Quantity,Board Feet'];

  for (const displaySpecies of labels) {
    for (const [structureName, csvItems] of Object.entries(grouped)) {
      const timberItems = convertToTimberListItems(csvItems);
      timberItems.forEach(item => {
        const speciesCell = speciesCellForExportItem(item, displaySpecies);
        const keepListedSpecies = !!displaySpecies.trim() && speciesCell !== displaySpecies.trim();
        const itemGrade = gradeCellForExportItem(item, speciesCell);
        const itemDryness = keepListedSpecies
          ? drynessCellForExportItem(item, DEFAULT_TIMBER_RFQ_DRYNESS_SPEC, true)
          : formatTimberMoistureLabel(item.moistureContent);
        rows.push(
          `"${structureName.replace(/"/g, '""')}","${item.name.replace(/"/g, '""')}","${speciesCell.replace(/"/g, '""')}","${itemGrade.replace(/"/g, '""')}","${itemDryness.replace(/"/g, '""')}","${(item.dimensions || '').replace(/"/g, '""')}",${item.quantity},${item.boardFeet}`,
        );
      });
    }
  }

  return rows.length > 1 ? rows.join('\n') : csvText;
}

/**
 * Prepare hub/supplier CSV text: Cadwork files become supplier format with Grade column;
 * header-based CSVs get Species/Material relabeled to the RFQ requested species
 * (full list — not filtered to matching staged-species rows).
 */
export function prepareTimberSupplierCsvText(
  csvText: string,
  requestedSpecies: string[] = [],
  options?: { trueDimensions?: boolean },
): string {
  if (isCadworkTimberCsv(csvText)) {
    return buildSupplierCsvTextFromCadworkCsv(csvText, requestedSpecies, options);
  }
  return applyRequestedSpeciesToTimberSupplierCsvText(csvText, requestedSpecies);
}

function formatCsvCell(value: string): string {
  if (/[",\n\r]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

/**
 * Relabel Species/Material on every data row to the RFQ requested species.
 * Multiple requested species duplicate the full list once per label.
 */
export function applyRequestedSpeciesToTimberSupplierCsvText(
  csvText: string,
  requestedSpecies: string[],
): string {
  if (!requestedSpecies.length) return csvText;
  const lines = csvText.split(/\r?\n/).filter(line => line.length > 0);
  if (lines.length < 2) return csvText;

  const headerCells = parseSimpleCsvRow(lines[0]!);
  const speciesIdx = headerCells.findIndex(cell => {
    const key = cell.trim().toLowerCase();
    return key === 'species' || key === 'material';
  });
  if (speciesIdx < 0) return csvText;

  const labels = requestedSpecies.map(s => s.trim()).filter(Boolean);
  if (!labels.length) return csvText;

  const nameIdx = headerCells.findIndex(cell => cell.trim().toLowerCase() === 'name');

  const out = [lines[0]!];
  for (const species of labels) {
    for (const line of lines.slice(1)) {
      const cells = parseSimpleCsvRow(line);
      while (cells.length <= speciesIdx) cells.push('');
      const listed = cells[speciesIdx] || '';
      const name = nameIdx >= 0 ? cells[nameIdx] || '' : '';
      if (!lineKeepsListedSpecies({ name, species: listed }, [species])) {
        cells[speciesIdx] = species;
      }
      out.push(cells.map(formatCsvCell).join(','));
    }
  }
  return out.join('\n');
}

/** @deprecated Prefer {@link applyRequestedSpeciesToTimberSupplierCsvText}. */
export function filterTimberSupplierCsvText(csvText: string, requestedSpecies: string[]): string {
  return applyRequestedSpeciesToTimberSupplierCsvText(csvText, requestedSpecies);
}

export function timberSupplierExportOptionsForOrg(
  project: Pick<RfqProject, 'timberSupplierInvites' | 'timberDrynessSpec'> | null | undefined,
  orgId: string | undefined,
): TimberSupplierExportOptions | undefined {
  if (!project?.timberSupplierInvites?.length || !orgId) return undefined;
  const entries = rfqProjectTimberSpeciesDrynessEntries(project, orgId);
  if (!entries.length) return undefined;
  const invite = project.timberSupplierInvites.find(i => i.supplierOrgId === orgId);
  const rules = structureSpeciesRulesFromInvite(invite);
  return { requestedSpeciesEntries: entries, ...(rules.length ? { structureSpecies: rules } : {}) };
}

function speciesNamesOnNodeSet(ns: ProposalNodeSet): string[] {
  const out: string[] = [];
  const add = (raw?: string) => {
    const species = raw?.trim();
    if (!species || out.some(existing => existing.toLowerCase() === species.toLowerCase())) return;
    out.push(species);
  };
  for (const item of ns.materials?.timberList ?? []) add(item.species);
  for (const alt of nodeSetExportAlternateSpecies(ns)) {
    add(alt.speciesName);
    for (const item of alt.timberList ?? []) add(item.species);
  }
  return out;
}

/**
 * Species rules for a supplier download of a package that was already sent.
 *
 * The live invite can later mark a structure as not priced (an empty
 * `structureSpecies` list). That must not blank the timber list frozen on the
 * package. Rules saved on the package win; a structure the package did not
 * narrow keeps the live invite's species, and falls back to the species on the
 * timber lines when the live invite would otherwise export nothing.
 */
export function timberSupplierExportOptionsForPackage(
  project: Pick<RfqProject, 'timberSupplierInvites' | 'timberDrynessSpec'> | null | undefined,
  pkg: Pick<RfqPackage, 'supplierOrgId' | 'structureSpecies' | 'scopeStructures' | 'proposalSnapshot'> | null | undefined,
  nodeSets?: ProposalNodeSet[],
): TimberSupplierExportOptions | undefined {
  const live = timberSupplierExportOptionsForOrg(project, pkg?.supplierOrgId);
  if (!pkg?.structureSpecies || !Object.keys(pkg.structureSpecies).length) return live;

  const frozenRules = structureSpeciesRulesFromPackage(pkg, []);
  const sets = nodeSets ?? (pkg.proposalSnapshot?.nodeSets ?? []).filter(ns => ns.enabled !== false);
  const rules: StructureSpeciesRule[] = [];
  const entries: TimberSpeciesDrynessEntry[] = [];
  const seen = new Set<string>();
  const addSpecies = (species: string) => {
    const trimmed = species.trim();
    const key = trimmed.toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    const liveEntry = live?.requestedSpeciesEntries?.find(entry => entry.species.trim().toLowerCase() === key);
    entries.push(
      liveEntry ?? {
        species: trimmed,
        drynessSpec: live?.requestedSpeciesEntries?.[0]?.drynessSpec ?? DEFAULT_TIMBER_RFQ_DRYNESS_SPEC,
      },
    );
  };

  for (const ns of sets) {
    const frozen = structureSpeciesRuleFor(ns, frozenRules);
    if (frozen) {
      frozen.species.forEach(addSpecies);
      rules.push({ id: ns.id, name: ns.name || frozen.name, species: frozen.species });
      continue;
    }
    const liveRule = structureSpeciesRuleFor(ns, live?.structureSpecies);
    if (liveRule?.species.length) {
      liveRule.species.forEach(addSpecies);
      rules.push({ id: ns.id, name: ns.name, species: liveRule.species });
      continue;
    }
    if (liveRule && !liveRule.species.length) {
      const lineSpecies = speciesNamesOnNodeSet(ns);
      lineSpecies.forEach(addSpecies);
      rules.push({ id: ns.id, name: ns.name, species: lineSpecies });
    }
  }

  return {
    ...(entries.length ? { requestedSpeciesEntries: entries } : live?.requestedSpeciesEntries?.length
      ? { requestedSpeciesEntries: live.requestedSpeciesEntries }
      : {}),
    ...(rules.length ? { structureSpecies: rules } : {}),
  };
}

/** Open the print target window synchronously (must run inside a click handler). */
export function openTimberPrintWindow(): Window | null {
  try {
    return window.open('about:blank', '_blank');
  } catch {
    return null;
  }
}

/** Write timber RFQ HTML into an already-open window and trigger print. */
export async function printTimberSupplierToWindow(
  printWindow: Window,
  state: SupplierTimberExportState,
  dealTitle: string | undefined,
  generatedAt?: Date,
  options?: TimberSupplierExportOptions,
): Promise<void> {
  const html = buildTimberSupplierRequestHtml(state, dealTitle, generatedAt, undefined, options);
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  await waitForDocumentImages(printWindow.document, 4000);
  await waitForNextPaint();
  printWindow.focus();
  printWindow.print();
}

/**
 * Open print window + export. Prefer {@link openTimberPrintWindow} + {@link printTimberSupplierToWindow}
 * from click handlers so pop-up blockers do not fire.
 */
export async function openTimberSupplierRequestPrint(
  state: SupplierTimberExportState,
  dealTitle: string | undefined,
  generatedAt?: Date,
  options?: TimberSupplierExportOptions,
): Promise<void> {
  const printWindow = openTimberPrintWindow();
  if (!printWindow) {
    throw new Error(TIMBER_SUPPLIER_POPUP_BLOCKED_MSG);
  }
  await printTimberSupplierToWindow(printWindow, state, dealTitle, generatedAt, options);
}
